import { useMemo } from 'react';
import merge from 'lodash/merge';

import useScreen from '@lyearn/core/hooks/useScreen';
import { cn } from '@lyearn/style';

import { ActionMenu, ActionMenuOverrides } from '../ActionMenu';
import { ACTION_MENU_LIST_ITEMS_CLASSNAME } from '../ActionMenu/components/ActionMenuListItems';
import { responsiveMenuItemStyles } from './styles';
import { ResponsiveActionMenuProps } from './types';

function ResponsiveActionMenu<T = string>(props: ResponsiveActionMenuProps<T>) {
  const { isMobile } = useScreen();
  const { items, renderHeader, overrides: overridesProp, ...rest } = props;

  const responsiveItems = useMemo(() => {
    if (isMobile) {
      return items.map((item) => {
        if (!item.type || item.type === 'item' || item.type === 'confirmation') {
          if (process.env.NODE_ENV === 'development' && (item.startIcon || item.StartIcon)) {
            console.error('startIcon and StartIcon are not supported in mobile view.');
          }

          return {
            ...item,
            StartIcon: item.EndIcon,
            startIcon: item.endIcon,
            EndIcon: undefined,
            endIcon: undefined,
            className: cn(responsiveMenuItemStyles, item.className),
            overrides: { Label: { variant: 'body-short-01' } },
          };
        }

        return item;
      });
    }

    return items;
  }, [isMobile, items]);

  const overrides = useMemo<ActionMenuOverrides>(
    () =>
      merge(
        {},
        {
          MenuList: { className: cn(ACTION_MENU_LIST_ITEMS_CLASSNAME, 'xs:pt-10 xs:pb-32') },
          PopoverContent: { renderAs: isMobile ? 'bottom-sheet' : 'popover' },
        },
        overridesProp,
      ),
    [isMobile, overridesProp],
  );

  return (
    <ActionMenu
      items={responsiveItems}
      overrides={overrides}
      renderHeader={isMobile ? renderHeader : undefined}
      {...rest}
    />
  );
}

export default ResponsiveActionMenu;
